import React, { useState } from "react";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState("");

  // Get the base URL from environment variables
  const INVENIO_UI_URL = process.env.REACT_APP_INVENIO_UI_URL;
  const LOST_PASSWORD_URL = `${INVENIO_UI_URL}/lost-password/`;

  // Email validation function
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Clear message after a delay
  const clearMessage = () => {
    setTimeout(() => {
      setMessage(null);
      setMessageType("");
    }, 5000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate email
    if (!email.trim()) {
      setMessage("Please enter your email address.");
      setMessageType("error");
      clearMessage();
      return;
    }

    if (!validateEmail(email.trim())) {
      setMessage("Please enter a valid email address.");
      setMessageType("error");
      clearMessage();
      return;
    }

    setIsLoading(true);
    setMessage(null);
    setMessageType("");

    try {
      // Get the backend form to extract all hidden fields
      const tokenResponse = await fetch(LOST_PASSWORD_URL, {
        method: "GET",
        credentials: "include",
      });

      if (!tokenResponse.ok) {
        throw new Error(`Failed to fetch form: ${tokenResponse.status}`);
      }

      const tokenHtml = await tokenResponse.text();

      // Extract all hidden input fields
      const hiddenInputs =
        tokenHtml.match(/<input[^>]*type="hidden"[^>]*>/g) || [];

      // Create form data with email
      const formData = new URLSearchParams();
      formData.append("email", email.trim().toLowerCase());

      // Add all hidden fields
      hiddenInputs.forEach((input) => {
        const nameMatch = input.match(/name="([^"]+)"/);
        const valueMatch = input.match(/value="([^"]*)"/);

        if (nameMatch && valueMatch) {
          const fieldName = nameMatch[1];
          const fieldValue = valueMatch[1];
          formData.append(fieldName, fieldValue);
        }
      });

      // Make the POST request
      const response = await fetch(LOST_PASSWORD_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Accept:
            "text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8",
          "User-Agent": "Mozilla/5.0 (compatible; React-App)",
        },
        credentials: "include",
        body: formData.toString(),
      });

      // Get response content
      const responseText = await response.text();

      // Check if response is ok
      if (!response.ok) {
        let errorMessage = `Server error (${response.status})`;

        try {
          const contentType = response.headers.get("content-type");
          if (contentType && contentType.includes("application/json")) {
            const errorData = JSON.parse(responseText);
            errorMessage = errorData.message || errorMessage;
          } else {
            errorMessage = `Server returned: ${response.status} ${response.statusText}`;
          }
        } catch (parseError) {
          // Ignore parse errors for error responses
        }

        setMessage(errorMessage);
        setMessageType("error");
        return;
      }

      // Look for success indicators in the response
      const successPatterns = [
        "Instructions to reset your password have been sent",
        "have been sent to",
        "sent to your email",
        "password reset instructions",
        "reset instructions have been sent",
        "instructions to reset your password",
        "email has been sent",
        "password reset email sent",
      ];

      let foundSuccess = false;
      const lowerResponseText = responseText.toLowerCase();

      for (const pattern of successPatterns) {
        if (lowerResponseText.includes(pattern.toLowerCase())) {
          foundSuccess = true;
          break;
        }
      }

      // Also check for redirect response (302/301)
      if (response.status === 302 || response.status === 301) {
        foundSuccess = true;
      }

      if (foundSuccess || response.ok) {
        setMessage(
          "Instructions to reset your password have been sent to your email address."
        );
        setMessageType("success");
        setEmail("");
      } else {
        setMessage(
          "Request processed, but unable to confirm email was sent. Please check your email."
        );
        setMessageType("success");
        setEmail("");
      }
    } catch (error) {
      if (error.name === "TypeError" && error.message.includes("fetch")) {
        setMessage(
          "Unable to connect to server. Please check your internet connection."
        );
      } else {
        setMessage(`Network error: ${error.message}`);
      }
      setMessageType("error");
    } finally {
      setIsLoading(false);
      if (messageType !== "success") {
        clearMessage();
      }
    }
  };

  const handleInputChange = (e) => {
    setEmail(e.target.value);
    // Clear any existing error messages when user starts typing
    if (message && messageType === "error") {
      setMessage(null);
      setMessageType("");
    }
  };

  // Check if button should be disabled
  const isButtonDisabled = isLoading || !email.trim();

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Logos Section */}
        <div style={styles.logosContainer}>
          <img
            src="/images/university-logo.png"
            alt="University Logo"
            style={styles.logo}
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
          <img
            src="/images/lrc-logo.png"
            alt="LRC Logo"
            style={styles.logo}
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        </div>

        <div style={styles.header}>
          <h1 style={styles.title}>Forgot Password?</h1>
          <p style={styles.subtitle}>
            Enter your email address and we'll send you a secure link to reset
            your password.
          </p>
        </div>

        {/* Message Display */}
        {message && (
          <div
            style={{
              ...styles.message,
              ...(messageType === "success"
                ? styles.messageSuccess
                : styles.messageError),
            }}
            role="alert"
            aria-live="polite"
          >
            {message}
          </div>
        )}

        {/* Show form only if no success message */}
        {messageType !== "success" && (
          <div>
            <div style={styles.formGroup}>
              <input
                type="email"
                value={email}
                onChange={handleInputChange}
                style={{
                  ...styles.input,
                  ...(message && messageType === "error" && !email.trim()
                    ? styles.inputError
                    : {}),
                }}
                placeholder="Enter your email address"
                required
                autoFocus
                disabled={isLoading}
                autoComplete="email"
                aria-label="Email address"
                aria-describedby={message ? "error-message" : undefined}
              />
            </div>

            <button
              type="submit"
              onClick={handleSubmit}
              style={{
                ...styles.button,
                ...(isButtonDisabled ? styles.buttonDisabled : {}),
              }}
              disabled={isButtonDisabled}
            >
              {isLoading ? (
                <span style={styles.loadingText}>
                  <span style={styles.spinner}> </span>
                  Sending...
                </span>
              ) : (
                "Send Reset Link"
              )}
            </button>
          </div>
        )}

        <div style={styles.footer}>
          <p style={styles.footerText}>Remember your password?</p>
          <a href="/login" style={styles.link}>
            Back to Sign In
          </a>
          <span style={styles.footerText}> or </span>
          <a href="/signup" style={styles.link}>
            Create Account
          </a>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
    background: "#225745",
    fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
  },
  card: {
    background: "rgba(255, 255, 255, 0.95)",
    borderRadius: "16px",
    boxShadow: "0 20px 40px rgba(133, 134, 40, 0.3)",
    padding: "40px",
    width: "100%",
    maxWidth: "450px",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(255, 255, 255, 0.2)",
  },
  logosContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "20px",
    marginBottom: "30px",
    paddingBottom: "20px",
    borderBottom: "1px solid #eee",
  },
  logo: {
    maxHeight: "100px",
    maxWidth: "120px",
    objectFit: "contain",
    filter: "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))",
  },
  header: {
    textAlign: "center",
    marginBottom: "30px",
  },
  title: {
    color: "#225745",
    fontSize: "2rem",
    fontWeight: "600",
    marginBottom: "8px",
    letterSpacing: "-0.5px",
  },
  subtitle: {
    color: "#666",
    fontSize: "0.95rem",
    lineHeight: "1.5",
    marginBottom: "0",
  },
  formGroup: {
    marginBottom: "20px",
  },
  input: {
    width: "100%",
    padding: "14px 16px",
    border: "2px solid #e1e5e9",
    borderRadius: "8px",
    fontSize: "1rem",
    transition: "all 0.3s ease",
    background: "#fff",
    outline: "none",
    boxSizing: "border-box",
  },
  inputError: {
    borderColor: "#dc3545",
    boxShadow: "0 0 0 3px rgba(220, 53, 69, 0.1)",
  },
  button: {
    width: "100%",
    padding: "14px",
    background: "#f4b952",
    color: "#000",
    border: "none",
    borderRadius: "8px",
    fontSize: "1rem",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s ease",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    position: "relative",
  },
  buttonDisabled: {
    opacity: "0.7",
    cursor: "not-allowed",
    background: "#ccc",
  },
  loadingText: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
  },
  spinner: {
    width: "16px",
    height: "16px",
    border: "2px solid transparent",
    borderTop: "2px solid #000",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  footer: {
    textAlign: "center",
    marginTop: "30px",
    paddingTop: "20px",
    borderTop: "1px solid #eee",
  },
  footerText: {
    color: "#666",
    margin: "0 5px",
  },
  link: {
    color: "#225745",
    textDecoration: "none",
    fontWeight: "500",
    transition: "color 0.3s ease",
  },
  message: {
    padding: "12px 16px",
    marginBottom: "20px",
    borderRadius: "8px",
    background: "#f8f9fa",
    borderLeft: "4px solid #225745",
    color: "#333",
  },
  messageSuccess: {
    background: "#d4edda",
    borderLeftColor: "#28a745",
    color: "#155724",
  },
  messageError: {
    background: "#f8d7da",
    borderLeftColor: "#dc3545",
    color: "#721c24",
  },
};

const spinnerStyle = document.createElement("style");
spinnerStyle.innerHTML = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(spinnerStyle);

export default ForgotPassword;
